import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { RowClickedEvent, GridReadyEvent } from "ag-grid-community";
import { Grid, AgGrid, AgGridReact, Flex, Text } from "@jsq/ds/core";
import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import { TransactionLookupDataSource } from "../../transactionLookupDataSource";
import { TransactionLookupFilters, TransactionRowItem } from "../../types";
import { getColumnDefs } from "./columnDefs";

/** Coalesce rapid filter edits (e.g. Entity ID typing) into one server request. */
export const FILTER_DEBOUNCE_MS = 300;

interface TransactionTableProps {
  filters: TransactionLookupFilters;
  onRowClick?: (transaction: TransactionRowItem) => void;
  onGridReady?: (event: GridReadyEvent) => void;
  gridApiRef?: React.MutableRefObject<AgGrid.GridApi | null>;
}

const DEFAULT_PAGE_SIZE = 50;

export const TransactionTable: React.FC<TransactionTableProps> = ({
  filters,
  onRowClick,
  onGridReady,
  gridApiRef,
}) => {
  const columnDefs = useMemo(() => getColumnDefs(), []);
  const [error, setError] = useState<string | null>(null);
  const [gridApi, setGridApi] = useState<AgGrid.GridApi | null>(null);

  const filterGenerationRef = useRef(0);
  const debouncedFilters = useDebouncedValue(filters, FILTER_DEBOUNCE_MS);

  const datasource = useMemo(() => {
    if (!debouncedFilters.arenaId) return null;
    const generation = ++filterGenerationRef.current;
    return new TransactionLookupDataSource(debouncedFilters, {
      onError: (message) => setError(message),
      onSuccess: () => setError(null),
      isStale: () => generation !== filterGenerationRef.current,
    });
  }, [debouncedFilters]);

  // AG Grid only reads serverSideDatasource from gridOptions on init; filter changes
  // must push a new datasource via the API or the grid keeps serving stale filters.
  // isStale drops in-flight getRows from prior generations so a slower arena-only
  // response cannot overwrite a newer entity-filtered page.
  useEffect(() => {
    setError(null);
    if (!gridApi || !datasource) return;
    gridApi.setGridOption("serverSideDatasource", datasource);
    gridApi.refreshServerSide({ purge: true });
  }, [gridApi, datasource]);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<TransactionRowItem>) => {
      if (event.data && onRowClick) {
        onRowClick(event.data);
      }
    },
    [onRowClick],
  );

  useEffect(() => {
    if (!filters.arenaId) {
      setGridApi(null);
    }
  }, [filters.arenaId]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
      if (gridApiRef) {
        gridApiRef.current = event.api;
      }
      onGridReady?.(event);
    },
    [gridApiRef, onGridReady],
  );

  if (!filters.arenaId) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        style={{ height: "400px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}
      >
        <Text color="secondary">Select an arena to search for transactions</Text>
      </Flex>
    );
  }

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    rowHeight: 48,
    suppressCellFocus: true,
    rowSelection: "single",
    rowModelType: "serverSide",
    getRowId: (params) => String((params.data as TransactionRowItem).glTransactionId),
    defaultColDef: {
      resizable: true,
      sortable: true,
    },
    overlayLoadingTemplate: '<span style="padding: 10px;">Loading transactions...</span>',
    overlayNoRowsTemplate:
      '<span style="padding: 10px;">No transactions found for the selected filters.</span>',
  };

  const sideBarConfig: AgGrid.SideBarDef = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressColumnFilter: false,
          suppressColumnSelectAll: false,
          suppressColumnExpandAll: true,
        },
      },
    ],
    defaultToolPanel: "",
  };

  return (
    <Flex flexDirection="column" style={{ gap: "8px", width: "100%" }}>
      {error && (
        <Flex
          style={{
            padding: "12px 16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "6px",
          }}
          alignItems="center"
          data-testid="transaction-table-error"
        >
          <Text style={{ color: "#856404", fontSize: "14px" }}>{error}</Text>
        </Flex>
      )}
      <Flex style={{ height: "600px", width: "100%" }}>
        <Grid
          gridOptions={gridOptions}
          columnDefs={columnDefs}
          onRowClicked={handleRowClicked}
          onGridReady={handleGridReady}
          pagination
          paginationPageSize={DEFAULT_PAGE_SIZE}
          paginationPageSizeSelector={[20, 50, 100]}
          cacheBlockSize={DEFAULT_PAGE_SIZE}
          sideBar={sideBarConfig}
          suppressColumnVirtualisation={true}
          data-testid="transaction-lookup-table"
        />
      </Flex>
    </Flex>
  );
};
